import { apiClient } from '../apiClient';

export interface MCQQuestion {
  id: string;
  assessment_id: string;
  question_text: string;
  options: string[];
  correct_option: number;
  sort_order: number;
  points: number;
}

export interface MCQQuestionStudent {
  id: string;
  assessment_id: string;
  question_text: string;
  options: string[];
  sort_order: number;
  points: number;
}

export interface UpsertMCQQuestionRequest {
  question_text: string;
  options: string[];
  correct_option: number;
  sort_order: number;
  points: number;
}

export interface MCQSubmitRequest {
  answers: number[];
}

export interface AssessmentSubmission {
  id: string;
  assessment_id: string;
  user_id: string;
  username?: string;
  answers: number[];
  score: number;
  max_score: number;
  status: string;
  submitted_at: string;
}

export interface AssignmentSubmission {
  id: string;
  assessment_id: string;
  user_id: string;
  submission_url: string;
  score: number | null;
  feedback: string | null;
  submitted_at: string;
  graded_at: string | null;
}

/**
 * QuizInfo is the read-only policy probe that tells the client whether an
 * assessment is taken through the timed-attempt lifecycle or the legacy
 * one-shot submit path. It must be consulted before starting anything:
 * POSTing an attempt to find out would create an attempt row and permanently
 * convert a legacy quiz to the attempt lifecycle.
 */
export interface QuizInfo {
  assessment_id: string;
  type: string;
  attempt_managed: boolean;
  duration_mins: number | null;
  max_attempts: number;
  questions_per_attempt: number | null;
  opens_at: string | null;
  closes_at: string | null;
  question_count: number;
  attempts_used: number;
  server_now: string;
}

/** One question as drawn for this attempt: options already permuted server-side. */
export interface AttemptQuestion {
  question_id: string;
  question_text: string;
  options: string[];
  points: number;
}

export interface AttemptPaper {
  attempt_id: string;
  attempt_no: number;
  max_attempts: number;
  expires_at: string | null;
  server_now: string;
  questions: AttemptQuestion[];
  /** Autosaved answers, keyed by question id, valued by DISPLAY index. */
  answers: Record<string, number>;
  resumed: boolean;
}

export interface AttemptSummary {
  attempt_id: string;
  attempt_no: number;
  status: string;
  started_at: string;
  expires_at: string | null;
  submitted_at: string | null;
  score: number | null;
  max_score: number | null;
}

export interface AttemptReviewItem {
  question_id: string;
  question_text: string;
  options: string[];
  chosen_index: number;
  /** -1 while the answer key is withheld (key_revealed === false). */
  correct_index: number;
  explanation: string;
  points: number;
  awarded: number;
}

export interface AttemptResult {
  attempt_id: string;
  score: number;
  max_score: number;
  best_score: number;
  attempt_no: number;
  max_attempts: number;
  /** False while attempts remain and the window is open: correct_index is -1. */
  key_revealed: boolean;
  review: AttemptReviewItem[];
}

export const assessmentsApi = {
  listQuestions: async (assessmentID: string): Promise<MCQQuestion[]> => {
    const { data } = await apiClient.get(`/api/v1/trainer/assessments/${assessmentID}/questions`);
    return data as MCQQuestion[];
  },
  createQuestion: async (assessmentID: string, req: UpsertMCQQuestionRequest): Promise<MCQQuestion> => {
    const { data } = await apiClient.post(`/api/v1/trainer/assessments/${assessmentID}/questions`, req);
    return data as MCQQuestion;
  },
  updateQuestion: async (assessmentID: string, questionID: string, req: UpsertMCQQuestionRequest): Promise<MCQQuestion> => {
    const { data } = await apiClient.put(`/api/v1/trainer/assessments/${assessmentID}/questions/${questionID}`, req);
    return data as MCQQuestion;
  },
  deleteQuestion: async (assessmentID: string, questionID: string): Promise<void> => {
    await apiClient.delete(`/api/v1/trainer/assessments/${assessmentID}/questions/${questionID}`);
  },
  listSubmissions: async (assessmentID: string): Promise<AssessmentSubmission[]> => {
    const { data } = await apiClient.get(`/api/v1/trainer/assessments/${assessmentID}/submissions`);
    return data as AssessmentSubmission[];
  },
  listQuestionsForStudent: async (assessmentID: string): Promise<MCQQuestionStudent[]> => {
    const { data } = await apiClient.get(`/api/v1/lms/assessments/${assessmentID}/questions`);
    return data as MCQQuestionStudent[];
  },
  submitMCQ: async (assessmentID: string, req: MCQSubmitRequest): Promise<AssessmentSubmission> => {
    const { data } = await apiClient.post(`/api/v1/lms/assessments/${assessmentID}/submit`, req);
    return data as AssessmentSubmission;
  },
  getMySubmission: async (assessmentID: string): Promise<AssessmentSubmission> => {
    const { data } = await apiClient.get(`/api/v1/lms/assessments/${assessmentID}/my-submission`);
    return data as AssessmentSubmission;
  },
  // ---- timed attempt lifecycle -------------------------------------------
  getQuizInfo: async (assessmentID: string): Promise<QuizInfo> => {
    const { data } = await apiClient.get(`/api/v1/lms/assessments/${assessmentID}/quiz-info`);
    return data as QuizInfo;
  },
  startAttempt: async (assessmentID: string): Promise<AttemptPaper> => {
    const { data } = await apiClient.post(`/api/v1/lms/assessments/${assessmentID}/attempts`, {});
    return data as AttemptPaper;
  },
  listMyAttempts: async (assessmentID: string): Promise<AttemptSummary[]> => {
    const { data } = await apiClient.get(`/api/v1/lms/assessments/${assessmentID}/attempts`);
    return (data ?? []) as AttemptSummary[];
  },
  getAttempt: async (attemptID: string): Promise<AttemptPaper> => {
    const { data } = await apiClient.get(`/api/v1/lms/assessments/attempts/${attemptID}`);
    return data as AttemptPaper;
  },
  saveAttemptAnswers: async (attemptID: string, answers: Record<string, number>): Promise<void> => {
    await apiClient.patch(`/api/v1/lms/assessments/attempts/${attemptID}`, { answers });
  },
  submitAttempt: async (attemptID: string, answers: Record<string, number>): Promise<AttemptResult> => {
    const { data } = await apiClient.post(`/api/v1/lms/assessments/attempts/${attemptID}/submit`, { answers });
    return data as AttemptResult;
  },

  submitAssignment: async (assessmentID: string, submissionUrl: string): Promise<AssignmentSubmission> => {
    const { data } = await apiClient.post(`/api/v1/lms/assessments/${assessmentID}/submit-assignment`, { submission_url: submissionUrl });
    return data as AssignmentSubmission;
  },
  getMyAssignmentSubmission: async (assessmentID: string): Promise<AssignmentSubmission | null> => {
    try {
      const { data } = await apiClient.get(`/api/v1/lms/assessments/${assessmentID}/my-assignment-submission`);
      return data as AssignmentSubmission;
    } catch {
      return null; // 404 means not yet submitted
    }
  },
};
